/**
 * Clinic CRM Messaging Interface - V2 Thread-Based + Real-Time SSE
 *
 * Desktop: Split-pane (Gmail style) — Left: Inbox, Right: Thread
 * Mobile:  Stacked navigation (WhatsApp style) — Inbox → Thread → Back
 *
 * Features:
 * - Real-time via SSE (message_created, message_read, thread_escalated, guard_flagged)
 * - Guard visualization (masked content + risk indicators)
 * - Cursor-based pagination for messages (before_id)
 * - Infinite scroll for inbox (offset-based)
 * - Risk scoring display on threads
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { useRBAC } from '../../context/RBACContext';
import MessagingAPI from '../../services/MessagingAPI';
import { useMessagingTransport } from '../../hooks/useMessagingTransport';
import ProviderDocumentShareDialog from '../../components/ProviderDocumentShareDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowLeft, MessageSquare, Send, Paperclip, FileText,
  CheckCheck, Check, AlertTriangle, Building2, User, Shield,
  DollarSign, ChevronRight, MoreVertical, Inbox, Search,
  RefreshCw, ExternalLink, Wifi, WifiOff, ChevronUp, Eye, EyeOff,
  Loader2, UserCheck, FileImage,
  Lock, Unlock, Info, AlertCircle, ShieldAlert
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import i18n from '../../i18n/i18n';
import { LeadStatusControl, LeadStatusBadge, LeadSourceBadge } from '../../components/LeadStatusControl';
import { CoordinatorAssign } from '../../components/CoordinatorAssign';
import { useCommunicationPolicy } from '../../hooks/useCommunicationPolicy';
import {
  WhatsAppChannelStrip,
  WhatsAppThreadBadge,
  isWhatsAppThread,
  whatsAppAnchorPhoneNumberId,
} from '../../components/messaging/WhatsAppChannelStrip';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../components/ui/tooltip';
import api from '../../utils/api';
import { selectPayableThreadQuote } from '../../utils/payableQuote';
import { PaymentReadinessBar } from '../../components/clinic/PaymentReadinessBar';
import { MedicalAccessRequestPanel } from '../../components/MedicalAccessRequestPanel';
import { isV3Enabled } from '../../utils/featureFlags';
import { CaseAssignmentBadge, ContactRevealIndicator } from '../../components/OfferDetailV3';

// ─── Constants ───────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────

// Stage config — keys are localized at render site via t()
// FASTFIX-PRE-OFFER-MESSAGING-UNBLOCK-01 (2026-06-12):  Owner product rule —
// canonical TreatX messaging is allowed from lead creation onward; only
// external contact / WhatsApp handoff is gated.  The "messaging unlocks
// once a quote is submitted" copy was an incorrect assumption and has
// been replaced with stage-context copy.
const STAGE_CONFIG = {
  discovery: { labelKey: 'provider.messaging.stage.discoveryLabel', color: 'bg-blue-50 border-blue-200 text-blue-700', icon: Info, hintKey: 'provider.messaging.stage.discoveryHint' },
  inquiry: { labelKey: 'provider.messaging.stage.inquiryLabel', color: 'bg-blue-50 border-blue-200 text-blue-700', icon: Info, hintKey: 'provider.messaging.stage.inquiryHint' },
  negotiation: { labelKey: 'provider.messaging.stage.negotiationLabel', color: 'bg-amber-50 border-amber-200 text-amber-700', icon: Shield, hintKey: 'provider.messaging.stage.negotiationHint' },
  post_payment: { labelKey: 'provider.messaging.stage.postPaymentLabel', color: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: Unlock, hintKey: 'provider.messaging.stage.postPaymentHint' },
};

const StageBanner = ({ policy }) => {
  const { t } = useTranslation();
  if (!policy || !policy.stage) return null;
  const cfg = STAGE_CONFIG[policy.stage];
  if (!cfg) return null;
  const Icon = cfg.icon;
  // WHATSAPP-AVAILABILITY-TRUTH-01 — the post-payment hint used to announce
  // that WhatsApp is available on the strength of the payment alone. Payment
  // decides business ELIGIBILITY; whether the provider's Meta/WhatsApp number
  // is actually connected is a separate canonical fact
  // (`whatsapp_channel_ready`). TreatX messaging is unaffected either way.
  const waClaim = policy.stage === 'post_payment' && policy.whatsapp_eligible;
  // Availability is claimed ONLY when readiness is established (`true`).
  // `false` = provably not connected; `unknown` = the status could not be read —
  // neither is allowed to promise the channel.
  const hint = waClaim
    ? (policy.whatsapp_channel_ready === true
        ? t('provider.messaging.stage.postPaymentHint')
        : t(policy.whatsapp_channel_reason === 'unknown'
            ? 'provider.messaging.stage.postPaymentWhatsappUnknown'
            : 'provider.messaging.stage.postPaymentWhatsappUnavailable'))
    : t(cfg.hintKey);
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2 border-b text-xs ${cfg.color}`} data-testid="stage-banner">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="font-semibold">{t(cfg.labelKey)}</span>
      <span className="opacity-70" data-testid="stage-banner-hint">{hint}</span>
      {policy.relay_active && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="ml-auto flex items-center gap-1 bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full px-2 py-0.5 text-[10px] font-medium cursor-default"
                data-testid="relay-active-badge"
              >
                <Shield className="h-3 w-3" />
                {t('provider.messaging.stage.maskedNumberBadge')}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px] text-center">
              {t('provider.messaging.stage.maskedNumberTooltip')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return i18n.t('provider.messaging.yesterday');
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const typeIcon = (t) => {
  if (t === 'lead') return <MessageSquare className="h-4 w-4" />;
  if (t === 'offer') return <DollarSign className="h-4 w-4" />;
  if (t === 'escalation') return <AlertTriangle className="h-4 w-4" />;
  return <Building2 className="h-4 w-4" />;
};

const typeBg = (t, s) => {
  if (s === 'escalated') return 'bg-red-500';
  if (t === 'lead') return 'bg-sky-500';
  if (t === 'offer') return 'bg-emerald-500';
  if (t === 'escalation') return 'bg-orange-500';
  return 'bg-slate-500';
};

const roleBadge = (r) => {
  const map = {
    patient: 'bg-violet-100 text-violet-700',
    provider_sales_rep: 'bg-sky-100 text-sky-700',
    provider_admin: 'bg-indigo-100 text-indigo-700',
    treatx_super_admin: 'bg-emerald-100 text-emerald-700',
    treatx_admin: 'bg-emerald-100 text-emerald-700',
    provider_coordinator: 'bg-teal-100 text-teal-700',
    provider_after_sales_rep: 'bg-cyan-100 text-cyan-700',
    provider_doctor: 'bg-amber-100 text-amber-700',
    system: 'bg-slate-100 text-slate-600',
  };
  return map[r] || 'bg-gray-100 text-gray-700';
};

// OF-001D #3 — consume the canonical role label resolver.
// Local map removed; `roleLabel(roleId)` is the SSOT.
const roleLabel = (r) => (r === 'system' ? i18n.t('provider.messaging.systemActorLabel') : localisedRoleLabel(r, i18n.t.bind(i18n)));

const CLINIC_ROLES = new Set(['provider_sales_rep', 'provider_admin', 'provider_coordinator', 'provider_after_sales_rep']);
// MESSAGING-FILTERS-TRUTH-01 — canonical lead stages that mean "an offer
// exists on this lead" (`OFFER_SENT` and its legacy `QUOTE_SENT` alias, plus
// everything downstream of acceptance). Mirrors the backend lead-status order;
// it adds no new stage and no second projection.
const PAGE_SIZE = 30;
const MSG_PAGE = 50;

// ─── Sales-ownership context ─────────────────────────────────────
// UAT-MSG-IDENTITY-DISPLAY-NAME lock 7 — "Unassigned" is meaningful only
// where the thread is inside a sales/lead ownership context. Coordination /
// medical / aftercare / system threads carry no sales owner by design (M-B).
export const isSalesContextThread = (thread) => {
  if (!thread) return false;
  if (thread.thread_purpose) return thread.thread_purpose === 'sales';
  return thread.context_type === 'lead' || thread.thread_type === 'lead';
};

const PURPOSE_LABEL_KEYS = {
  coordination: 'provider.messaging.purposeCoordination',
  medical: 'provider.messaging.purposeMedical',
  aftercare: 'provider.messaging.purposeAftercare',
};

// ─── Ownership Badge ─────────────────────────────────────────────
const OwnershipBadge = ({ thread, clinicAgents = [] }) => {
  const { t } = useTranslation();
  if (thread.transfer_status === 'transferred') {
    return (
      <Badge data-testid={`ownership-badge-${thread.id}`} className="bg-purple-100 text-purple-700 text-[10px] h-[18px] px-1.5 flex items-center gap-0.5">
        <ArrowLeft className="h-2.5 w-2.5 rotate-180" /> {t('provider.messaging.transferredBadge')}
      </Badge>
    );
  }
  if (thread.assigned_agent_id && thread.assigned_agent_id !== 'SYSTEM_POOL') {
    // Canonical identity match: `assigned_agent_id` == `user_id`.
    // `assigned_agent_name` is the display projection stored on the
    // thread doc at assign time — never an identity authority, and a
    // raw id is NEVER shown as a fallback.
    const agent = clinicAgents.find(a => a.user_id === thread.assigned_agent_id || a.id === thread.assigned_agent_id);
    const displayName = agent?.name || agent?.display_name || thread.assigned_agent_name || t('provider.messaging.assignedRepresentativeFallback');
    return (
      <Badge data-testid={`ownership-badge-${thread.id}`} className="bg-sky-100 text-sky-700 text-[10px] h-[18px] px-1.5 flex items-center gap-0.5 max-w-[160px]">
        <UserCheck className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{displayName}</span>
      </Badge>
    );
  }
  if (thread.clinic_id && isSalesContextThread(thread)) {
    return (
      <Badge data-testid={`ownership-badge-${thread.id}`} className="bg-orange-100 text-orange-600 text-[10px] h-[18px] px-1.5 flex items-center gap-0.5">
        <Inbox className="h-2.5 w-2.5" /> {t('provider.messaging.unassignedBadge')}
      </Badge>
    );
  }
  const purposeKey = PURPOSE_LABEL_KEYS[thread.thread_purpose];
  if (thread.clinic_id && purposeKey) {
    return (
      <Badge data-testid={`ownership-badge-${thread.id}`} className="bg-slate-100 text-slate-600 text-[10px] h-[18px] px-1.5 flex items-center gap-0.5">
        {t(purposeKey)}
      </Badge>
    );
  }
  return null;
};

// ─── Thread Group Header ─────────────────────────────────────────
const ThreadGroupHeader = ({ label, count, color }) => (
  <div className={`px-4 py-1.5 ${color} border-b flex items-center justify-between sticky top-0 z-[5]`} data-testid={`thread-group-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
    <span className="text-[10px] font-medium opacity-60">{count}</span>
  </div>
);

// P0-SHARE-RECIPIENT-IDENTITY-01 — one shared provider-facing recipient
// identity projection for the thread list, the thread header AND the
// secure-share modal. See `utils/threadRecipientIdentity.js`.
import {
  resolveThreadPatientLabel,
  localiseMaskedLabel,
  INTERNAL_PARTICIPANT_LABEL_RE,
} from '../../utils/threadRecipientIdentity';
// TREATX-MSG-UX-SYSTEM-EVENTS-01 — presentation-only grouping.
import {
  SystemEventGroup,
  groupConsecutiveSystemEvents,
} from '../../components/messaging/SystemEventGroup';
// Localised canonical role labels (no second role vocabulary).
import { localisedRoleLabel, isSystemPoolAssignee } from '../../utils/roleLabelI18n';

export { resolveThreadPatientLabel, INTERNAL_PARTICIPANT_LABEL_RE };

// ─── ThreadListItem ──────────────────────────────────────────────
const ThreadListItem = ({ thread, isActive, onClick, userId, clinicAgents, leadIndex }) => {
  const { t } = useTranslation();
  const unread = thread.unread_counts?.[userId] || 0;
  const esc = thread.status === 'escalated';
  const flagged = thread.circumvention_flag;
  const patient = thread.participants?.find((p) => p.role === 'patient');
  const leadInfo = thread.context_type === 'lead' ? leadIndex?.get?.(thread.context_id) : null;
  const displayName = resolveThreadPatientLabel(thread, leadIndex, t('provider.messaging.unknownPatient'), t('provider.messaging.patient'));
  const displaySubject = leadInfo?.treatment_display_name || thread.subject || leadInfo?.treatment_interest || t('provider.messaging.genericInquirySubject', { type: thread.thread_type });

  return (
    <div
      onClick={onClick}
      data-testid={`thread-item-${thread.id}`}
      className={`px-4 py-3 border-b cursor-pointer transition-colors ${
        isActive ? 'bg-sky-50/80 border-l-[3px] border-l-sky-600' : 'hover:bg-slate-50'
      } ${esc ? 'bg-red-50/40' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 ${typeBg(thread.thread_type, thread.status)}`}>
          {typeIcon(thread.thread_type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4
              className="font-semibold text-slate-800 truncate text-sm leading-tight"
              data-testid={`thread-item-patient-${thread.id}`}
            >
              {displayName}
            </h4>
            <span className="text-[11px] text-slate-400 whitespace-nowrap">{fmt(thread.last_message_at)}</span>
          </div>
          <p className="text-xs text-slate-500 truncate mt-0.5" data-testid={`conversation-subject-${thread.id}`}>{displaySubject}</p>
          {/* UAT-MSG-IDENTITY-SOURCE-AWARE-01 — a protected TreatX lead shows
              its stable alias above plus the request reference here, so two
              requests from the same person never look like one conversation. */}
          {thread.identity_masked && thread.request_reference && (
            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1" data-testid={`thread-identity-protected-${thread.id}`}>
              <Shield className="h-2.5 w-2.5" />
              <span>{t('provider.messaging.identityProtectedRow', { reference: thread.request_reference })}</span>
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px] capitalize h-[18px] px-1.5">{thread.thread_type}</Badge>
            {thread.lead_status && <LeadStatusBadge status={thread.lead_status} />}
            {esc && <Badge className="bg-red-500 text-white text-[10px] h-[18px] px-1.5">{t('provider.messaging.escalatedBadge')}</Badge>}
            {flagged && (
              <Badge className="bg-amber-500 text-white text-[10px] h-[18px] px-1.5 flex items-center gap-0.5">
                <Shield className="h-2.5 w-2.5" /> {t('provider.messaging.riskBadge')}
              </Badge>
            )}
            {thread.lead_source && <LeadSourceBadge source={thread.lead_source} />}
            {/* MID-04-B / FE-01 — channel identity from the thread payload */}
            <WhatsAppThreadBadge thread={thread} />
            <OwnershipBadge thread={thread} clinicAgents={clinicAgents} />
            {/* V3: Case assignment badge */}
            {isV3Enabled() && thread.case_id && (
              <Badge variant="outline" className="text-[10px] h-[18px] px-1.5 text-amber-700 border-amber-300 bg-amber-50" data-testid={`v3-case-badge-${thread.id}`}>
                {t('provider.messaging.caseBadge')}
              </Badge>
            )}
          </div>
          <p className="text-[13px] text-slate-400 truncate mt-1 leading-snug">
            {thread.last_message_sender && <span className="font-medium text-slate-500">{localiseMaskedLabel(thread.last_message_sender, t('provider.messaging.patient'), t('provider.messaging.systemActorLabel'))}: </span>}
            {thread.last_message_preview || t('provider.messaging.noMessagesYet')}
          </p>
        </div>
        {unread > 0 && (
          <Badge data-testid={`unread-badge-${thread.id}`} className="bg-sky-600 text-white shrink-0 text-xs">{unread}</Badge>
        )}
      </div>
    </div>
  );
};

// ─── Conversation context strip ──────────────────────────────────
// Operational context a rep needs mid-conversation, from data the page
// already holds: canonical lead source + payment state (projected
// server-side from the SAME lead the identity gate reads) and the offer
// already fetched for this thread. A payment ATTEMPT is never shown as
// verified, and nothing here depends on the patient's identity.
const ThreadContextStrip = ({ thread, threadQuote }) => {
  const { t } = useTranslation();
  if (!thread) return null;
  const category = thread.lead_source_category;
  const paymentState = thread.payment_state;
  const offerTotal = threadQuote?.financial_summary?.total;
  // Only render a status we can actually name in the UI language — never a
  // raw backend enum.
  const offerStatusLabel = threadQuote?.status
    ? t(`provider.messaging.context.offerStatus.${String(threadQuote.status).toLowerCase()}`,
        { defaultValue: '' })
    : '';
  const sourceLabel = category === 'provider_sourced'
    ? t('provider.messaging.context.sourceProvider')
    : category
      ? t('provider.messaging.context.sourceTreatx')
      : t('provider.messaging.context.sourceUnknown');

  return (
    <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto min-w-0 max-w-full sm:flex-wrap sm:overflow-visible scrollbar-none" data-testid="thread-context-strip">
      <Badge
        variant="outline"
        className="text-[10px] h-[18px] px-1.5 text-slate-600 border-slate-300 shrink-0 whitespace-nowrap"
        data-testid="context-source-badge"
      >
        {sourceLabel}
      </Badge>
      {typeof offerTotal === 'number' && offerTotal > 0 && (
        <Badge
          variant="outline"
          className="text-[10px] h-[18px] px-1.5 text-slate-700 border-slate-300 shrink-0 whitespace-nowrap"
          data-testid="context-offer-badge"
        >
          {t('provider.messaging.context.offer')} {threadQuote?.currency || 'EUR'}{' '}
          {offerTotal.toLocaleString()}
          {offerStatusLabel ? ` · ${offerStatusLabel}` : ''}
        </Badge>
      )}
      {paymentState === 'verified' && (
        <Badge
          className="bg-emerald-100 text-emerald-700 text-[10px] h-[18px] px-1.5 flex items-center gap-0.5 shrink-0 whitespace-nowrap"
          data-testid="context-payment-verified"
        >
          <CheckCheck className="h-2.5 w-2.5" />
          {t('provider.messaging.context.paymentVerified')}
        </Badge>
      )}
      {paymentState === 'pending' && (
        <Badge
          className="bg-amber-100 text-amber-700 text-[10px] h-[18px] px-1.5 shrink-0 whitespace-nowrap"
          data-testid="context-payment-pending"
        >
          {t('provider.messaging.context.paymentPending')}
        </Badge>
      )}
      {thread.identity_masked && (
        <Badge
          variant="outline"
          className="text-[10px] h-[18px] px-1.5 text-slate-500 border-slate-300 shrink-0 whitespace-nowrap max-w-[55vw] sm:max-w-none truncate sm:whitespace-normal sm:h-auto sm:py-0.5 sm:leading-tight"
          title={t('provider.messaging.context.identityProtected')}
          data-testid="context-identity-protected"
        >
          {t('provider.messaging.context.identityProtected')}
        </Badge>
      )}
    </div>
  );
};

// ─── ThreadHeader ────────────────────────────────────────────────
const ThreadHeader = ({ thread, onResolve, onViewLead, onBack, riskSummary, onThreadUpdate, isAdmin, clinicAgents = [], leadIndex, threadQuote }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const patient = thread?.participants?.find((p) => p.role === 'patient');
  const isLead = thread?.thread_type === 'lead';
  // Canonical identity match: `assigned_agent_id` == `user_id` in the
  // clinic team roster. `assigned_agent_name` on the thread doc is the
  // display fallback, not an identity authority.
  const assignedAgent = clinicAgents.find(a => a.user_id === thread?.assigned_agent_id || a.id === thread?.assigned_agent_id);
  const assignedSnapshot = isSystemPoolAssignee(thread) ? '' : thread?.assigned_agent_name;
  const assignedDisplayName = assignedAgent?.name || assignedAgent?.display_name || assignedSnapshot
    || (thread?.assigned_agent_id && thread.assigned_agent_id !== 'SYSTEM_POOL' ? t('provider.messaging.assignedRepresentativeFallback') : '');
  const leadInfo = thread?.context_type === 'lead' ? leadIndex?.get?.(thread.context_id) : null;
  const displayName = resolveThreadPatientLabel(thread, leadIndex, t('provider.messaging.patient'), t('provider.messaging.patient'));
  const displaySubject = leadInfo?.treatment_display_name || thread?.subject || leadInfo?.treatment_interest || t('provider.messaging.conversation');

  const handleAssign = async (agentId) => {
    try {
      // Assignee display name is resolved server-side from canonical staff identity.
      const res = await api.put(`/v2/threads/${thread.id}/assign`, { agent_id: agentId });
      onThreadUpdate?.(res.data?.thread || { ...thread, assigned_agent_id: agentId });
    } catch (e) { console.error('Assign error:', e); }
    setAssignOpen(false);
    setMenuOpen(false);
  };

  return (
    <div className="bg-white border-b px-4 py-3 sticky top-0 z-10" data-testid="thread-header">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="lg:hidden p-2 hover:bg-slate-100 rounded-full" data-testid="thread-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-sky-500 rounded-full flex items-center justify-center text-white font-semibold shrink-0 text-sm">
          {displayName?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2
              className="font-semibold text-slate-800 truncate text-sm"
              data-testid="thread-header-patient"
            >
              {displayName}
            </h2>
            <WhatsAppThreadBadge thread={thread} />
            {thread?.status === 'escalated' && <Badge className="bg-red-500 text-white text-[10px]">{t('provider.messaging.escalatedBadge')}</Badge>}
            {riskSummary?.circumvention_flag && (
              <Badge className="bg-amber-500/90 text-white text-[10px] flex items-center gap-0.5">
                <Shield className="h-2.5 w-2.5" /> {t('provider.messaging.riskScoreBadge', { score: riskSummary.risk_score_average?.toFixed(0) || 0 })}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs text-slate-500 truncate" data-testid="thread-header-subject">{displaySubject}</p>
            {/* UAT-MSG-IDENTITY-SOURCE-AWARE-01 — the active conversation must
                be unmistakable, so a protected thread names its request
                reference next to the alias in the header too. */}
            {thread?.identity_masked && thread?.request_reference && (
              <span className="text-[11px] font-medium text-slate-500 shrink-0" data-testid="thread-header-request-reference">
                {thread.request_reference}
              </span>
            )}
            <ThreadContextStrip thread={thread} threadQuote={threadQuote} />
            {thread?.transfer_status === 'transferred' ? (
              <Badge data-testid="transferred-badge" className="bg-purple-100 text-purple-700 text-[10px] h-[18px] px-1.5 shrink-0 flex items-center gap-0.5">
                <ArrowLeft className="h-2.5 w-2.5 rotate-180" /> {t('provider.messaging.transferredBadge')}
              </Badge>
            ) : (assignedAgent || (thread?.assigned_agent_id && thread?.assigned_agent_id !== 'SYSTEM_POOL')) ? (
              // The assign control already states the assignee for admins;
              // other roles have no assign control, so they get this badge.
              isAdmin ? null : (
                <Badge data-testid="assignment-badge" className="bg-sky-100 text-sky-700 text-[10px] h-[18px] px-1.5 shrink-0 flex items-center gap-0.5">
                  <UserCheck className="h-2.5 w-2.5" /> {assignedDisplayName || t('provider.messaging.assignedBadge')}
                </Badge>
              )
            ) : thread?.assigned_agent_id === 'SYSTEM_POOL' ? (
              <Badge data-testid="system-pool-badge" className="bg-amber-100 text-amber-700 text-[10px] h-[18px] px-1.5 shrink-0 flex items-center gap-0.5">
                <Inbox className="h-2.5 w-2.5" /> {t('provider.messaging.systemPoolBadge')}
              </Badge>
            ) : thread?.clinic_id ? (
              <Badge data-testid="unassigned-badge" className="bg-orange-100 text-orange-600 text-[10px] h-[18px] px-1.5 shrink-0 flex items-center gap-0.5">
                <Inbox className="h-2.5 w-2.5" /> {t('provider.messaging.unassignedBadge')}
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="relative">
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 hover:bg-slate-100 rounded-full" data-testid="thread-actions-btn">
            <MoreVertical className="h-5 w-5 text-slate-400" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[200px] z-20">
              {thread?.status !== 'resolved' && (
                <button
                  onClick={() => { onResolve(); setMenuOpen(false); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-green-600"
                  data-testid="resolve-thread-btn"
                >
                  <CheckCheck className="h-4 w-4" /> {t('provider.messaging.markResolved')}
                </button>
              )}
              {isAdmin && clinicAgents.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setAssignOpen(!assignOpen)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-sky-600"
                    data-testid="assign-thread-btn"
                  >
                    <UserCheck className="h-4 w-4" /> {assignedAgent ? t('provider.messaging.reassignAgent') : t('provider.messaging.assignAgent')}
                  </button>
                  {assignOpen && (
                    <div className="border-t py-1 max-h-40 overflow-y-auto" data-testid="agent-picker">
                      {clinicAgents.map(ag => {
                        const agLabel = ag.name || ag.display_name || t('provider.messaging.assignedRepresentativeFallback');
                        const agRole = localisedRoleLabel(
                          ag.role || (Array.isArray(ag.roles) ? ag.roles[0] : ''), t,
                        );
                        return (
                        <button
                          key={ag.id}
                          onClick={() => handleAssign(ag.user_id || ag.id)}
                          className={`w-full px-4 py-1.5 text-left text-xs hover:bg-sky-50 flex items-center gap-2 ${(ag.user_id || ag.id) === thread?.assigned_agent_id ? 'bg-sky-50 font-medium' : ''}`}
                          data-testid={`assign-agent-${ag.user_id || ag.id}`}
                        >
                          <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold shrink-0">
                            {agLabel.charAt(0).toUpperCase()}
                          </div>
                          <span className="truncate">{agLabel}</span>
                          <span className="text-[10px] text-slate-400 ml-auto">{agRole}</span>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => { onViewLead?.(); setMenuOpen(false); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-600"
                data-testid="view-lead-btn"
              >
                <ExternalLink className="h-4 w-4" /> {t('provider.messaging.viewLead')}
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Lead Operations Controls */}
      {isLead && (
        <div className="flex items-center gap-2 mt-2 ml-12 flex-nowrap overflow-x-auto min-w-0 sm:flex-wrap sm:overflow-visible scrollbar-none" data-testid="thread-lead-controls">
          {/* TREATX-CLINIC-MESSAGES-UX-CORRECTION-10D: hide the
              "Set Source" control on the messaging workspace per owner
              UX. Status + Assign remain because they are operational
              actions a clinic admin takes mid-conversation. Source is
              only editable from the CRM lead view. */}
          <LeadStatusControl thread={thread} onUpdate={onThreadUpdate} hideSource />
          {isAdmin && (
            <CoordinatorAssign thread={thread} onUpdate={onThreadUpdate} />
          )}
        </div>
      )}
    </div>
  );
};

// ─── MessageBubble ───────────────────────────────────────────────
const MessageBubble = ({ message, fromClinic, showSender, isAdmin, onViewWhatsAppMedia }) => {
  const { t } = useTranslation();
  const [showOriginal, setShowOriginal] = useState(false);
  const masked = message.masking_applied;
  const riskHigh = (message.risk_score || 0) >= 50;

  // Inquiry / Request note — NOT a chat message, distinct styling
  if (message.message_type === 'inquiry_note' || message.message_type === 'request_note') {
    return (
      <div className="flex justify-center my-4" data-testid={`msg-inquiry-note-${message.id}`}>
        <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 max-w-md w-full">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">{t('provider.messaging.requestNoteBadge')}</span>
          </div>
          <p className="text-sm text-amber-900">{message.content}</p>
          <p className="text-xs text-amber-500 mt-1.5">{fmt(message.created_at)}</p>
        </div>
      </div>
    );
  }

  // System message
  if (message.message_type === 'system') {
    const meta = message.system_metadata || {};
    const isStageChange = message.system_event === 'communication_stage_changed';

    if (isStageChange) {
      const stageCfg = STAGE_CONFIG[meta.new_stage] || {};
      const StageIcon = stageCfg.icon || Info;
      return (
        <div className="flex justify-center my-4" data-testid={`msg-stage-${meta.new_stage}`}>
          <div className={`border rounded-xl px-4 py-2.5 max-w-md flex items-center gap-2.5 ${stageCfg.color || 'bg-slate-50 border-slate-200 text-slate-700'}`}>
            <StageIcon className="h-4 w-4 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">{message.content}</p>
              <p className="text-xs opacity-60 mt-0.5">{fmt(message.created_at)}</p>
            </div>
          </div>
        </div>
      );
    }

    // TREATX-PROVIDER-DOCUMENT-SHARING-12A — clinic-side card for
    // provider-originated docs the clinic just shared. Same canonical
    // event; just a confirmation surface on the sender side.
    const sharedIds = meta.attachment_ids || [];
    const hasProviderDocs = meta.provider_document_shared && sharedIds.length > 0;
    if (hasProviderDocs) {
      return (
        <div className="flex justify-center my-3" data-testid={`msg-system-${message.id}`}>
          <div className="border rounded-xl px-4 py-3 max-w-md bg-emerald-50 border-emerald-200 text-left">
            <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-emerald-600" />
              {t('provider.messaging.documentSharedWithPatient')}
            </p>
            {Array.isArray(meta.file_names) && meta.file_names.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {meta.file_names.slice(0, 4).map((fn, i) => (
                  <li key={i} className="text-xs text-emerald-900 flex items-center gap-1.5">
                    <FileText className="h-3 w-3 text-emerald-600 shrink-0" />
                    <span className="truncate">{fn}</span>
                  </li>
                ))}
              </ul>
            )}
            {meta.document_category && (
              <p className="text-[10px] text-emerald-600 mt-1 uppercase tracking-wide">
                {String(meta.document_category).replace(/_/g, ' ')}
              </p>
            )}
            <p className="text-[10px] text-emerald-500 mt-1.5">{fmt(message.created_at)}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center my-3" data-testid={`msg-system-${message.id}`}>
        <div className={`rounded-full px-4 py-1.5 max-w-md text-center text-xs ${
          riskHigh ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-slate-100 border border-slate-200 text-slate-500'
        }`}>
          {riskHigh && <Shield className="h-3 w-3 inline mr-1 -mt-0.5" />}
          {message.content}
          <span className="block text-[10px] mt-0.5 opacity-60">{fmt(message.created_at)}</span>
        </div>
      </div>
    );
  }

  const displayContent = showOriginal && message.content_original ? message.content_original : message.content;

  return (
    <div className={`flex ${fromClinic ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`msg-${message.id}`}>
      <div className="max-w-[78%] md:max-w-[65%]">
        {showSender && !fromClinic && (
          <div className="flex items-center gap-1.5 mb-0.5 ml-1">
            <Badge className={`text-[10px] px-1.5 h-[16px] ${roleBadge(message.sender_role)}`}>{roleLabel(message.sender_role)}</Badge>
            <span className="text-[11px] text-slate-400">{localiseMaskedLabel(message.sender_name, t('provider.messaging.patient'), t('provider.messaging.systemActorLabel'))}</span>
          </div>
        )}
        <div className={`rounded-2xl px-3.5 py-2.5 ${
          fromClinic ? 'bg-sky-600 text-white rounded-br-sm' : 'bg-white border shadow-sm rounded-bl-sm'
        }`}>
          <p className={`text-sm whitespace-pre-wrap leading-relaxed ${fromClinic ? 'text-white' : 'text-slate-800'}`}>
            {displayContent}
          </p>
          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.attachments.map((att, i) => {
                // P1-WA-MEDIA-01 — an inbound WhatsApp attachment arrives as a
                // `media_id` only; the bytes are fetched on demand through the
                // ACL-enforced media route. Everything else keeps the previous
                // read-only label.
                const label = att.filename || att.file_name
                  || (att.media_id ? t('provider.messaging.whatsappMediaFallback', { type: att.type || 'media' }) : '');
                const isWhatsAppMedia = att.retrieval === 'whatsapp_media' && !!att.media_id;
                if (isWhatsAppMedia) {
                  return (
                    <button
                      key={att.media_id || i}
                      type="button"
                      onClick={() => onViewWhatsAppMedia?.(att.media_id, label, att.mime_type)}
                      className={`w-full flex items-center gap-2 p-1.5 rounded-lg text-xs text-left transition-colors ${fromClinic ? 'bg-white/15 hover:bg-white/25' : 'bg-slate-50 hover:bg-slate-100'}`}
                      data-testid={`whatsapp-media-attachment-${att.media_id}`}
                      title={t('provider.messaging.whatsappMediaOpen')}
                    >
                      <FileText className={`h-3.5 w-3.5 shrink-0 ${fromClinic ? 'text-white/70' : 'text-slate-500'}`} />
                      <span className={`truncate underline decoration-dotted ${fromClinic ? 'text-white/90' : 'text-slate-700'}`}>{label}</span>
                    </button>
                  );
                }
                return (
                  <div key={att.id || i} className={`flex items-center gap-2 p-1.5 rounded-lg text-xs ${fromClinic ? 'bg-white/15' : 'bg-slate-50'}`}>
                    <FileText className={`h-3.5 w-3.5 ${fromClinic ? 'text-white/70' : 'text-slate-500'}`} />
                    <span className={`truncate ${fromClinic ? 'text-white/80' : 'text-slate-600'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Offer reference */}
          {message.offer_reference && (
            <div className={`mt-2 p-2.5 rounded-lg border text-xs ${fromClinic ? 'bg-white/15 border-white/20' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-1 font-medium mb-1">
                <DollarSign className="h-3 w-3" /> {message.offer_reference.treatment_name}
              </div>
              <span className="font-bold">{message.offer_reference.currency || 'EUR'} {message.offer_reference.amount?.toLocaleString()}</span>
            </div>
          )}
          {/* Guard masking indicator */}
          {masked && (
            <div className={`flex items-center gap-1.5 mt-1.5 text-[11px] ${fromClinic ? 'text-white/60' : 'text-amber-600'}`}>
              <Shield className="h-3 w-3" />
              <span>{t('provider.messaging.contentFiltered')}</span>
              {isAdmin && message.content_original && (
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className={`ml-1 underline decoration-dotted ${fromClinic ? 'text-white/70 hover:text-white' : 'text-amber-700 hover:text-amber-800'}`}
                  data-testid={`toggle-original-${message.id}`}
                >
                  {showOriginal ? <><EyeOff className="h-3 w-3 inline" /> {t('provider.messaging.hideOriginal')}</> : <><Eye className="h-3 w-3 inline" /> {t('provider.messaging.viewOriginal')}</>}
                </button>
              )}
            </div>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-0.5 ${fromClinic ? 'justify-end' : 'justify-start'} px-1`}>
          <span className="text-[10px] text-slate-400">{fmt(message.created_at)}</span>
          {fromClinic && (
            /* MID-04-C / FE-04 — a failed WhatsApp transport is shown as
               failed instead of silently looking sent. */
            (message.overall_status === 'failed'
              || message.whatsapp_transport?.state === 'failed')
              ? <span className="inline-flex items-center gap-0.5 text-red-500" data-testid="message-delivery-failed">
                  <AlertTriangle className="h-3 w-3" />
                </span>
            : message.overall_status === 'read' ? <CheckCheck className="h-3 w-3 text-sky-500" />
            : message.overall_status === 'delivered' ? <CheckCheck className="h-3 w-3 text-slate-300" />
            : <Check className="h-3 w-3 text-slate-300" />
          )}
          {(message.risk_score || 0) > 0 && !fromClinic && (
            <span className={`text-[10px] font-medium ${riskHigh ? 'text-amber-600' : 'text-slate-400'}`}>
              {t('provider.messaging.riskInlineLabel', { score: message.risk_score })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ComposeBox ──────────────────────────────────────────────────
const ComposeBox = ({ onSend, disabled, senderName, onTyping, typingUser, threadId, recipientLabel, onSharedDoc, actorRole, locale }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const typingTimerRef = useRef(null);
  // TREATX-PROVIDER-DOCUMENT-SHARING-12A: clinic-side attach affordance
  // opens the canonical Secure Share dialog for provider-originated
  // documents (clinic → patient). Reuses the same backend pipeline
  // (`/api/clinic/thread-attachments` writes to medical_attachments +
  // attachment_access + attachment_access_logs — no parallel store).
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    // M-C — preserve the typed draft when onSend throws (403/400).
    // Only clear on a resolved success.
    try {
      const result = onSend(trimmed);
      if (result && typeof result.then === 'function') {
        await result;
      }
      setText('');
    } catch {
      // keep the draft — the parent's handleSend already showed a toast.
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    if (onTyping && !typingTimerRef.current) {
      onTyping();
      typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
    }
  };

  return (
    <div className="bg-white border-t px-4 py-3 sticky bottom-0" data-testid="compose-box">
      {typingUser && (
        <div className="text-[11px] text-slate-400 mb-1.5 flex items-center gap-1 animate-pulse" data-testid="typing-indicator">
          <span className="inline-flex gap-0.5">
            <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
          {t('provider.messaging.someoneTyping', { name: typingUser.userName || t('provider.messaging.someoneFallback') })}
        </div>
      )}
      <div className="flex items-center gap-1.5 mb-1.5 text-[11px] text-slate-400">
        <User className="h-3 w-3" /> {t('provider.messaging.replyingAs', { name: senderName })}
        {/* V3: Channel indicator */}
        {isV3Enabled() && (
          <span className="ml-auto flex items-center gap-1 text-blue-500">
            <MessageSquare className="h-3 w-3" /> {t('provider.messaging.channelPatientThread')}
          </span>
        )}
      </div>
      <div className="flex items-end gap-2">
        {/* TREATX-PROVIDER-DOCUMENT-SHARING-12A: paperclip now opens the
            canonical Secure Share dialog for provider-originated docs.
            Reuses the same attachment_access / audit infrastructure
            (no parallel store, no parallel UI). Replaced the 10D
            informative-only modal. */}
        <button
          type="button"
          onClick={() => setShareDialogOpen(true)}
          disabled={disabled || !threadId}
          className="p-2 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-slate-500 hover:text-blue-600 transition"
          data-testid="attach-btn"
          title={t('provider.messaging.attachDocTitle')}
          aria-label={t('provider.messaging.attachDocAria')}
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <textarea
          value={text}
          onChange={handleChange}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t('provider.messaging.composePlaceholder')}
          rows={1}
          className="flex-1 px-4 py-2.5 border rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm"
          disabled={disabled}
          data-testid="message-input"
        />
        <Button
          onClick={send}
          disabled={!text.trim() || disabled}
          className="rounded-full h-10 w-10 p-0 bg-sky-600 hover:bg-sky-700 shrink-0"
          data-testid="send-btn"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {/* TREATX-PROVIDER-DOCUMENT-SHARING-12A — Secure provider-document
          share dialog. Mounted only while open; recipient is auto-
          resolved from the active thread so the clinic user never
          enters a patient_id. */}
      <ProviderDocumentShareDialog
        key={threadId || 'no-thread'}
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShared={(payload) => { onSharedDoc && onSharedDoc(payload); }}
        threadId={threadId}
        recipientLabel={recipientLabel}
        actorRole={actorRole}
        locale={locale}
      />
    </div>
  );
};

// ─── Stats Bar ───────────────────────────────────────────────────
const StatsBar = ({ threads, userId }) => {
  const { t } = useTranslation();
  const unread = threads.reduce((s, tr) => s + (tr.unread_counts?.[userId] || 0), 0);
  const escalated = threads.filter((tr) => tr.status === 'escalated').length;
  const active = threads.filter((tr) => tr.status === 'active').length;

  return (
    <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-white border-b" data-testid="stats-bar">
      {[
        { val: threads.length, label: t('provider.messaging.statsTotal'), color: 'text-slate-800' },
        { val: unread, label: t('provider.messaging.statsUnread'), color: 'text-sky-600' },
        { val: active, label: t('provider.messaging.statsActive'), color: 'text-emerald-600' },
        { val: escalated, label: t('provider.messaging.statsEscalated'), color: 'text-red-500' },
      ].map((s) => (
        <div key={s.label} className="text-center">
          <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
          <p className="text-[10px] text-slate-400">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Empty States ────────────────────────────────────────────────
// MSG-09 P1-4 — a 403 on the clinic-wide endpoint and a network failure
// must NEVER look like "you have no conversations". `notice` distinguishes
// the three states; it never reveals whether threads exist.
const EmptyInbox = ({ notice = null, onRetry = null }) => {
  const { t } = useTranslation();
  const isError = notice === 'error';
  const isScoped = notice === 'scoped';
  const key = isError ? 'error' : isScoped ? 'scopedEmpty' : null;
  return (
    <div
      className="flex flex-col items-center justify-center h-full p-8 text-center"
      data-testid={isError ? 'inbox-error-state' : isScoped ? 'inbox-scoped-empty-state' : 'empty-inbox'}
    >
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${isError ? 'bg-red-50' : isScoped ? 'bg-amber-50' : 'bg-slate-100'}`}>
        {isError ? <AlertCircle className="h-7 w-7 text-red-400" />
          : isScoped ? <ShieldAlert className="h-7 w-7 text-amber-500" />
          : <Inbox className="h-7 w-7 text-slate-400" />}
      </div>
      <h3 className="font-semibold text-slate-700 text-sm mb-1">
        {t(key ? `provider.messaging.inboxNotice.${key}Title` : 'provider.messaging.emptyInbox.title')}
      </h3>
      <p className="text-xs text-slate-400 max-w-[240px]">
        {t(key ? `provider.messaging.inboxNotice.${key}Body` : 'provider.messaging.emptyInbox.body')}
      </p>
      {isError && onRetry && (
        <Button size="sm" variant="outline" className="mt-3" onClick={onRetry} data-testid="inbox-error-retry-btn">
          {t('provider.messaging.inboxNotice.retry')}
        </Button>
      )}
    </div>
  );
};

const EmptyThread = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center" data-testid="empty-thread">
      <div className="w-14 h-14 bg-sky-50 rounded-full flex items-center justify-center mb-3">
        <MessageSquare className="h-7 w-7 text-sky-400" />
      </div>
      <h3 className="font-semibold text-slate-700 text-sm mb-1">{t('provider.messaging.emptyThread.title')}</h3>
      <p className="text-xs text-slate-400">{t('provider.messaging.emptyThread.body')}</p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const ClinicMessagesV2 = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clinicUser, isClinicAdmin: isClinicAdminLegacy, isSalesRep: isSalesRepLegacy } = useClinicData();
  const { activeClinicId: rbacClinicId, isClinicAdmin: isClinicAdminRBAC, isDoctor, isSuperAdmin, hasRole, activeRoleId } = useRBAC();

  // Fallback to sessionStorage
  const stored = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('treatx_clinic_user')); } catch { return null; }
  }, []);
  const user = clinicUser || stored;

  // Use RBAC clinic context as primary source, fallback to legacy clinicUser
  const clinicId = rbacClinicId || user?.clinicId;
  const userId = user?.id;
  const isAdmin = isClinicAdminRBAC || isSuperAdmin || isClinicAdminLegacy || user?.role === 'treatx_admin';
  const isSalesRole = hasRole('provider_sales_rep') || isSalesRepLegacy || user?.role === 'salesRep';
  const userRole = isAdmin ? 'provider_admin' : isSalesRole ? 'provider_sales_rep' : isDoctor ? 'provider_doctor' : 'provider_admin';
  const userName = user?.name || 'Clinic Staff';

  // ── State ──────────────────────────────────────────────────────
  const [threads, setThreads] = useState([]);
  const [threadFilter, setThreadFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [threadsLoading, setThreadsLoading] = useState(true);
  // MSG-09 P1-4 — null | 'scoped' (403 → participant-scoped fallback) | 'error'
  const [listNotice, setListNotice] = useState(null);
  const [threadsOffset, setThreadsOffset] = useState(0);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);

  // Phase C: Inbox scope (All / Mine / Unassigned)
  const [inboxScope, setInboxScope] = useState('all');
  const [clinicAgents, setClinicAgents] = useState([]);
  // UAT-PROD-PROVIDER-MESSAGING-ACCESS-01 — deep-link resolution state.
  // `?lead=` / `?thread=` may target a canonical thread outside the
  // first loaded page; we resolve it through the ACL-enforced canonical
  // lookup and surface a real state instead of a silent console warning.
  const [deepLinkError, setDeepLinkError] = useState(null);
  const deepLinkAttemptRef = useRef(null);

  // FASTFIX-SALESREP-MESSAGE-ICON-OPENS-CANONICAL-THREAD-02 (2026-06-12):
  // lead-id → { patient_name, treatment_interest } resolved from the
  // canonical /clinics/{clinicId}/leads endpoint (the same one the Lead
  // Management table reads) so the messaging UI shows the SAME patient
  // identity the rep already sees in Lead Detail — not the privacy-
  // masked clinic-side participant label.  No new endpoint introduced.
  const [leadIndex, setLeadIndex] = useState(() => new Map());

  useEffect(() => {
    if (!clinicId) return;
    let alive = true;
    api.get(`/clinics/${clinicId}/leads`, { params: { limit: 200, view: 'index' } })
       .then(res => {
         if (!alive) return;
         const next = new Map();
         for (const l of (res.data?.leads || [])) {
           if (l?.id) next.set(l.id, {
             patient_name: l.patient_name || null,
             treatment_interest: l.treatment_interest || null,
           });
         }
         setLeadIndex(next);
       })
       .catch(() => { /* additive: silent fallback to participant label */ });
    return () => { alive = false; };
  }, [clinicId]);

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [hasMoreMsgs, setHasMoreMsgs] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [riskSummary, setRiskSummary] = useState(null);

  const [sending, setSending] = useState(false);
  const [mobileShowInbox, setMobileShowInbox] = useState(true);
  const [typingUsers, setTypingUsers] = useState({}); // threadId → { userId, userName, ts }

  // Communication policy for active thread
  const { policy, refetch: refetchPolicy } = useCommunicationPolicy(activeThreadId);

  // TREATX-PROVIDER-DOCUMENT-SHARING-12A — patient-readable label
  // resolved once per thread; the ProviderDocumentShareDialog uses
  // this so the clinic user never sees an internal patient_id.
  // P0-SHARE-RECIPIENT-IDENTITY-01 — the share modal recipient label is
  // now the SAME lead/context-aware projection the thread list + header
  // render (masked for Marketplace leads, real where the Provider is
  // already entitled), instead of the raw participant label which could
  // surface an internal `Patient-xxxxx` identifier.
  const activeThreadPatientLabel = useMemo(
    () => resolveThreadPatientLabel(activeThread, leadIndex, t('provider.messaging.patient'), t('provider.messaging.patient')),
    [activeThread, leadIndex, t],
  );

  // MID-04-B — originating connected Provider number for the active
  // WhatsApp thread. Resolved from the EXISTING provider Meta connection
  // projection (`GET /api/meta/connection/status` → whatsapp.phone_assets),
  // fetched lazily only when a WhatsApp thread is open. No new endpoint and
  // no new authority; the thread anchor (`provider_account_id`) stays the
  // single source of the sender identity.
  const [waSenderNumbers, setWaSenderNumbers] = useState({});
  useEffect(() => {
    if (!isWhatsAppThread(activeThread)) return;
    if (Object.keys(waSenderNumbers).length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/meta/connection/status');
        const assets = res.data?.connection?.whatsapp?.phone_assets || [];
        if (cancelled) return;
        const map = {};
        assets.forEach((a) => {
          if (a?.phone_number_id) map[a.phone_number_id] = a.display_phone_number || null;
        });
        setWaSenderNumbers(map);
      } catch (_err) { /* silent — the strip falls back to the generic copy */ }
    })();
    return () => { cancelled = true; };
  }, [activeThread, waSenderNumbers]);

  const activeThreadWaSenderNumber = useMemo(() => {
    const pid = whatsAppAnchorPhoneNumberId(activeThread);
    return pid ? (waSenderNumbers[pid] || null) : null;
  }, [activeThread, waSenderNumbers]);

  // Coordinator / Clinic Admin own clinic WhatsApp communication and may
  // answer before assignment (server-side authority:
  // MessagingService.can_send_to_thread + messaging.supervise). Assigned
  // Sales Reps send as participants. This is a UI projection only.
  const canSendInActiveThread = useMemo(() => {
    if (!activeThread) return false;
    if (['provider_admin', 'provider_coordinator'].includes(activeRoleId)) return true;
    const parts = activeThread.participants || [];
    return parts.some((pt) => pt.user_id === userId && pt.status !== 'retired');
  }, [activeThread, activeRoleId, userId]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);

  // Deep-link: ?thread=<id> from command palette
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Fetch threads (paged) — Phase C: respects inboxScope ───────
  //
  // B-2 / INV-M11 — canonical single-fetch guarantee.
  //
  // Prior implementation closed `fetchThreads` over `threadsOffset`.
  // Every successful fetch bumped `threadsOffset` → the useCallback
  // identity churned → every effect that had `fetchThreads` in its
  // dep-array re-ran → we observed ~6 duplicate `/api/v2/threads/
  // clinic/{id}` requests within seconds of page load. Fix: read /
  // write the offset via a ref so the callback identity is stable,
  // and use functional setState for both `threads` and `threadsOffset`
  // so we never depend on a stale closure. `threadsOffset` state is
  // preserved for badge/display consumers; the ref is the single
  // authority the fetcher uses to compute `off`.
  const threadsOffsetRef = useRef(0);
  const inFlightThreadsFetch = useRef(false);
  const pendingResetPromiseRef = useRef(null);
  const fetchThreads = useCallback(async (reset = false) => {
    if (!clinicId) return;
    // B-2 / INV-M11 — single-flight guard covers BOTH reset and
    // paged fetches. React.StrictMode double-mounts effects in dev,
    // which used to fire two identical `fetchThreads(true)` calls at
    // the same millisecond → duplicate network requests. If a reset
    // is already in flight, join its promise instead of firing a
    // second one. Paged fetches (reset=false) simply drop.
    if (reset && pendingResetPromiseRef.current) {
      return pendingResetPromiseRef.current;
    }
    if (!reset && inFlightThreadsFetch.current) return;
    inFlightThreadsFetch.current = true;
    const _work = (async () => {
      try {
        if (reset) {
          setThreadsLoading(true);
          threadsOffsetRef.current = 0;
        }
        const off = reset ? 0 : threadsOffsetRef.current;
        let res;
        if (inboxScope === 'mine') {
          const r = await api.get(`/v2/threads/mine?limit=${PAGE_SIZE}&offset=${off}`);
          res = r.data;
          setListNotice(null);
        } else if (inboxScope === 'unassigned') {
          const r = await api.get(`/v2/threads/unassigned?limit=${PAGE_SIZE}&offset=${off}`);
          res = r.data;
          setListNotice(null);
        } else {
          // M-C — `/v2/threads/clinic/{id}` requires `messaging.supervise`.
          // Ordinary sales reps hit 403 here and fall back to the
          // participant-scoped personal inbox.
          // MSG-09 P1-4 — the fallback is NO LONGER SILENT: an
          // authorization boundary must not render as an empty inbox.
          // We surface a scoped-visibility notice instead. No permission
          // is broadened and the retired INV-C20 clinic wildcard is NOT
          // restored — the rep still sees only what they participate in.
          try {
            res = await MessagingAPI.getClinicThreads({ clinicId, limit: PAGE_SIZE, offset: off });
            setListNotice(null);
          } catch (clinicErr) {
            if (clinicErr?.response?.status === 403) {
              const r = await api.get(`/v2/threads?limit=${PAGE_SIZE}&offset=${off}`);
              res = r.data;
              setListNotice('scoped');
            } else {
              throw clinicErr;
            }
          }
        }
        const fetched = res.threads || [];
        if (reset) {
          setThreads(fetched);
          threadsOffsetRef.current = fetched.length;
          setThreadsOffset(fetched.length);
        } else {
          setThreads((prev) => {
            const ids = new Set(prev.map((t) => t.id));
            return [...prev, ...fetched.filter((t) => !ids.has(t.id))];
          });
          threadsOffsetRef.current += fetched.length;
          setThreadsOffset((o) => o + fetched.length);
        }
        setHasMoreThreads(fetched.length >= PAGE_SIZE);
      } catch (err) {
        console.error('fetchThreads:', err);
        // MSG-09 P1-4 — a transport/server failure is an ERROR state, not
        // an empty inbox. The message is generic: it never discloses
        // whether conversations exist.
        setListNotice('error');
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.loadConversationsFailed'), variant: 'destructive' });
      } finally {
        setThreadsLoading(false);
        inFlightThreadsFetch.current = false;
        if (reset) pendingResetPromiseRef.current = null;
      }
    })();
    if (reset) pendingResetPromiseRef.current = _work;
    return _work;
  }, [clinicId, inboxScope]);

  // ── Fetch messages (cursor-based) ──────────────────────────────
  const fetchMessages = useCallback(async (threadId, beforeId = null) => {
    try {
      if (!beforeId) setMsgsLoading(true);
      else setLoadingOlder(true);

      const res = await MessagingAPI.getMessages({ threadId, limit: MSG_PAGE, beforeId });
      const fetched = res.messages || [];

      if (beforeId) {
        setMessages((prev) => [...fetched, ...prev]);
      } else {
        setMessages(fetched);
        setActiveThread(res.thread || null);
      }
      setHasMoreMsgs(fetched.length >= MSG_PAGE);

      // Mark read
      if (userId && !beforeId) {
        MessagingAPI.markMessagesRead({ threadId, userId }).catch(() => {});
      }
    } catch (err) {
      console.error('fetchMessages:', err);
      toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.loadMessagesFailed'), variant: 'destructive' });
    } finally {
      setMsgsLoading(false);
      setLoadingOlder(false);
    }
  }, [userId]);

  // ── Risk summary ───────────────────────────────────────────────
  const fetchRisk = useCallback(async (threadId) => {
    try {
      const r = await MessagingAPI.getThreadRisk(threadId);
      setRiskSummary(r);
    } catch { setRiskSummary(null); }
  }, []);

  // ── Real-time transport handlers (WS or SSE) ────────────────────
  const transportHandlers = useMemo(() => ({
    onMessageCreated: (evt) => {
      if (evt.thread_id === activeThreadId) {
        fetchMessages(evt.thread_id);
      }
      fetchThreads(true);
    },
    onMessageRead: (evt) => {
      if (evt.thread_id === activeThreadId) {
        setMessages((prev) => prev.map((m) =>
          m.id === evt.message_id ? { ...m, overall_status: 'read' } : m
        ));
      }
    },
    onThreadEscalated: (evt) => {
      setThreads((prev) => prev.map((t) =>
        t.id === evt.thread_id ? { ...t, status: 'escalated' } : t
      ));
      if (evt.thread_id === activeThreadId) {
        setActiveThread((t) => t ? { ...t, status: 'escalated' } : t);
      }
    },
    onGuardFlagged: (evt) => {
      setThreads((prev) => prev.map((t) =>
        t.id === evt.thread_id ? { ...t, circumvention_flag: true } : t
      ));
      if (evt.thread_id === activeThreadId) {
        fetchRisk(evt.thread_id);
      }
    },
    onTyping: (evt) => {
      if (evt.thread_id === activeThreadId && evt.user_id !== userId) {
        setTypingUsers((prev) => ({
          ...prev,
          [evt.thread_id]: { userId: evt.user_id, userName: evt.user_name, ts: Date.now() },
        }));
      }
    },
    onQuotaExceeded: (evt) => {
      toast({ title: t('provider.messaging.toast.quotaLimitTitle'), description: evt.detail || t('provider.messaging.toast.quotaExceededDefault'), variant: 'destructive' });
    },
  }), [activeThreadId, fetchMessages, fetchThreads, fetchRisk, userId]);

  const { isConnected, transport, joinThread, leaveThread, sendMessage: wsSend, sendTyping } =
    useMessagingTransport(userId, transportHandlers);

  // ── Initial load + clinic/scope reload ─────────────────────────
  //
  // B-2 / INV-M11 — SINGLE canonical mount+trigger effect. The prior
  // implementation had TWO separate effects (`[clinicId, fetchThreads]`
  // and `[inboxScope, fetchThreads]`), both of which re-ran on every
  // fetchThreads identity churn — the second half of the duplicate
  // fetch storm. `fetchThreads` is now stable (see the callback dep
  // list), so a single effect keyed on the real semantic triggers
  // (clinic change, scope change) fires the fetch exactly once per
  // legitimate trigger event. On clinic change we also reset the
  // active-thread state so we don't render a stale conversation from
  // the previous workspace.
  const prevClinicRef = useRef(clinicId);
  useEffect(() => {
    if (!clinicId) return;
    if (prevClinicRef.current !== clinicId) {
      prevClinicRef.current = clinicId;
      setActiveThreadId(null);
      setActiveThread(null);
      setMessages([]);
      setRiskSummary(null);
      setMobileShowInbox(true);
    }
    fetchThreads(true);
  }, [clinicId, inboxScope, fetchThreads]);

  // Phase C: Load clinic team roster for the assignment display.
  //
  // CANONICAL AUTHORITY:  `leads.assigned_agent_id` (mirrored onto
  // `message_threads.assigned_agent_id` by the `assign_lead` thread-
  // sync in `clinic_lead_routes.py`). This effect resolves that ID to
  // a display name via the SAME roster Lead Management uses. Do NOT
  // use `/clinic/agents` here — on many clinics that endpoint returns
  // an empty list; `/clinic/team` is the canonical team roster and
  // carries the identity as `user_id` (matching `assigned_agent_id`).
  //
  // A previous batch left a debug kill-switch (`const _t = '';
  // if (!_t) return;`) in this effect, blocking the roster from
  // ever loading. That is what caused the "Unassigned" badge in
  // Provider Messaging while Lead Management showed the correct
  // Sales Rep — the underlying thread field was correct, the FE
  // simply could not resolve the id to a name.
  useEffect(() => {
    if (!clinicId) return;
    api.get(`/clinic/team`)
      .then(({ data }) => {
        const members = Array.isArray(data?.members) ? data.members : [];
        // Normalize the shape used by the badge/header: canonical
        // identity key is `user_id`; keep `id` as a bridge for any
        // legacy consumer of this state.
        setClinicAgents(members.map(m => ({
          id: m.user_id,
          user_id: m.user_id,
          name: m.name || '',
          email: m.email || '',
          role: m.role || '',
        })));
      })
      .catch(() => setClinicAgents([]));
  }, [clinicId]);

  // ── Scroll to bottom on new messages ───────────────────────────
  // TREATX-CLINIC-MESSAGES-UX-CORRECTION-10D: use `block:'nearest'` so
  // the scroll is contained inside the messages panel and never moves
  // the document/window scroll position. Owner observed the page
  // jumping all the way down to the marketing footer when a thread
  // was selected — that was caused by the default `scrollIntoView`
  // walking the DOM up to the document scroller.
  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messages.length > prevMsgCount.current && !loadingOlder) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
    prevMsgCount.current = messages.length;
  }, [messages.length, loadingOlder]);

  // ── Infinite scroll for inbox ──────────────────────────────────
  const inboxSentinelRef = useRef(null);
  useEffect(() => {
    const el = inboxSentinelRef.current;
    if (!el || !hasMoreThreads || threadsLoading) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchThreads(false);
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMoreThreads, threadsLoading, fetchThreads]);

  // ── Select thread ──────────────────────────────────────────────
  const prevThreadRef = useRef(null);
  const selectThread = useCallback((thread) => {
    // Leave previous thread room (WS)
    if (prevThreadRef.current) leaveThread(prevThreadRef.current);
    prevThreadRef.current = thread.id;

    setActiveThreadId(thread.id);
    setActiveThread(thread);
    // MESSAGING-FILTERS-TRUTH-01 — the deep-link outcome banner describes the
    // conversation that could not be opened. Opening another conversation
    // makes it stale, so it is cleared here instead of lingering above an
    // unrelated thread.
    setDeepLinkError(null);
    setMobileShowInbox(false);
    setMessages([]);
    setRiskSummary(null);
    // B-2 / INV-M11 — local unread patch (mirror of M1.a on patient
    // inbox). `fetchMessages` fires the server-side mark-read, and
    // the durable authority (`messages.read_by`) is updated with the
    // recomputed unread count on the thread. But the visible badge
    // on the LEFT panel is driven by the in-memory `threads` state,
    // which never got refreshed on click → badges stayed stale for
    // the whole session (RES-2-E). Patch locally so the badge
    // vanishes immediately without waiting for a full inbox refetch
    // (which would re-open the duplicate-fetch class of bug).
    if (userId) {
      setThreads((prev) => prev.map((t) => (
        t.id === thread.id
          ? { ...t, unread_counts: { ...(t.unread_counts || {}), [userId]: 0 } }
          : t
      )));
    }
    fetchMessages(thread.id);
    if (thread.circumvention_flag) fetchRisk(thread.id);

    // Join new thread room (WS)
    joinThread(thread.id);
  }, [fetchMessages, fetchRisk, joinThread, leaveThread, userId]);

  // ── Deep-link: auto-select thread from ?thread=<id> OR ?lead=<id> ──
  // FASTFIX-SALESREP-MESSAGE-ICON-OPENS-CANONICAL-THREAD-01 (2026-06-12):
  // Sales Rep → My Assigned Leads → Message icon now passes ?lead=<id>.
  // We resolve the canonical thread via the existing context_type='lead'
  // + context_id relationship (SSOT already established by
  // CANONICAL-ASSIGNMENT-THREAD-SYNC-01).  We force the inbox scope to
  // 'all' so the deep-link works regardless of the user's filter state,
  // never auto-create a new thread, and warn to the console if the
  // canonical thread is unexpectedly missing.
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    const leadParam = searchParams.get('lead');
    if (!threadParam && !leadParam) return;
    if (activeThreadId) return;
    if (leadParam && inboxScope !== 'all') {
      // Force "All" so the canonical lead thread is in scope regardless
      // of whether it's assigned to the current user.
      setInboxScope('all');
      return; // fetchThreads will re-run; this effect will re-evaluate
    }

    let cancelled = false;
    (async () => {
      let match = null;
      if (threadParam) {
        match = threads.find(t => t.id === threadParam);
      } else if (leadParam) {
        match = threads.find(
          t => t.context_type === 'lead' && t.context_id === leadParam,
        );
      }
      if (match) {
        selectThread(match);
        setSearchParams({}, { replace: true }); // clean URL
        return;
      }
      if (threadsLoading) return;

      // Not in the loaded page → canonical ACL-enforced lookup. We never
      // create a thread because the UI could not find one.
      const key = threadParam || leadParam;
      if (deepLinkAttemptRef.current === key) return;
      deepLinkAttemptRef.current = key;
      try {
        const res = leadParam
          ? await api.get('/v2/threads/resolve', {
            params: { context_type: 'lead', context_id: leadParam },
          })
          : await api.get(`/v2/threads/${threadParam}`);
        const resolved = res?.data;
        if (cancelled || !resolved?.id) return;
        setThreads(prev => (
          prev.some(t => t.id === resolved.id) ? prev : [resolved, ...prev]
        ));
        selectThread(resolved);
        setSearchParams({}, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setDeepLinkError(
          status === 403 ? 'not_authorized'
            : status === 404 ? 'not_found'
              : 'error',
        );
      }
    })();
    return () => { cancelled = true; };
  }, [threads, threadsLoading, searchParams, activeThreadId, selectThread,
      setSearchParams, inboxScope]);

  // ── Load older messages ────────────────────────────────────────
  const loadOlder = useCallback(() => {
    if (!activeThreadId || loadingOlder || !hasMoreMsgs || messages.length === 0) return;
    fetchMessages(activeThreadId, messages[0]?.id);
  }, [activeThreadId, loadingOlder, hasMoreMsgs, messages, fetchMessages]);

  // ── Send message ───────────────────────────────────────────────
  const handleSend = useCallback(async (content) => {
    if (!activeThreadId || !userId) return;
    try {
      setSending(true);
      // TREATX-CLINIC-MESSAGES-UX-CORRECTION-10D: REST is the canonical
      // send path. WebSocket is real-time delivery on top of REST and
      // must NEVER be the only path — the previous "WS-then-fallback"
      // pattern caused silent failures whenever the WS handshake had
      // succeeded but server-side message persistence on the WS branch
      // hadn't (the owner observed a typed "Merhaba" never appearing
      // in the thread). Always go through REST; the server will fan
      // out the new_message event to other connected clients.
      const sendResult = await MessagingAPI.sendMessage({
        threadId: activeThreadId,
        senderId: userId,
        senderRole: userRole,
        senderName: userName,
        content,
        messageType: 'text',
      });
      // MID-04-C / FE-03 — the canonical message was persisted but the
      // external WhatsApp delivery failed. Never show a plain success and
      // never make the provider retype (that produced duplicates): the
      // message is refreshed into the thread carrying its failed transport
      // state, and the real error code is surfaced.
      if (sendResult?.whatsapp_delivery === 'failed') {
        toast({
          title: t('provider.messaging.whatsapp.deliveryFailedTitle'),
          description: t('provider.messaging.whatsapp.deliveryFailedBody', {
            code: sendResult.error_code || 'META_SEND_FAILED',
          }),
          variant: 'destructive',
        });
      }
      await fetchMessages(activeThreadId);
      fetchThreads(true);
    } catch (err) {
      console.error('sendMessage:', err);
      // M-C — supervisor read is not participant send. Surface the
      // 403 clearly instead of the generic "send failed" toast that
      // used to leave the composer looking as if it succeeded.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast({
          title: t('provider.messaging.toast.errorTitle'),
          description: detail || 'You must be an active participant in this thread to send. Ask a clinic admin to assign you.',
          variant: 'destructive',
        });
      } else {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.sendFailed'), variant: 'destructive' });
      }
      // Re-throw so the composer preserves the typed draft.
      throw err;
    } finally {
      setSending(false);
    }
  }, [activeThreadId, userId, userRole, userName, fetchMessages, fetchThreads, t]);

  // ── Payment Request ─────────────────────────────────────────────
  const [threadQuote, setThreadQuote] = useState(null);
  const [paymentRequesting, setPaymentRequesting] = useState(false);

  // MESSAGING-READINESS-TRUTH-01 — selection rule lives in
  // `selectPayableThreadQuote` (exported, unit-tested).
  const fetchThreadQuote = useCallback(async (thread) => {
    if (!thread?.context_id) { setThreadQuote(null); return; }
    try {
      const r = await api.get(`/clinic/quote-requests/${thread.context_id}`, {
        headers: {},
      });
      if (r.status >= 200 && r.status < 300) {
        setThreadQuote(selectPayableThreadQuote(r.data?.quotes));
      } else {
        setThreadQuote(null);
      }
    } catch { setThreadQuote(null); }
  }, []);

  // Refetch quote when thread changes
  useEffect(() => {
    if (activeThread?.context_type === 'lead') {
      fetchThreadQuote(activeThread);
    } else {
      setThreadQuote(null);
    }
  }, [activeThread?.id, fetchThreadQuote]);

  const handleRequestPayment = useCallback(async () => {
    if (!threadQuote?.id) return;
    try {
      setPaymentRequesting(true);
      const API = process.env.REACT_APP_BACKEND_URL;
      const _t = '';
      const r = await api.post(`/clinic/quotes/${threadQuote.id}/request-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({}),
      });
      const data = r.data;
      if (r.status >= 200 && r.status < 300 && data.success) {
        toast({ title: t('provider.messaging.toast.paymentRequestedTitle'), description: t('provider.messaging.toast.paymentRequestedBody') });
        setThreadQuote(prev => prev ? { ...prev, payment_request_status: 'requested' } : null);
        if (activeThreadId) fetchMessages(activeThreadId);
      } else {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: data.detail || data.error || t('provider.messaging.toast.requestPaymentFailed'), variant: 'destructive' });
      }
    } catch (err) {
      console.error('requestPayment:', err);
      toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.requestPaymentFailed'), variant: 'destructive' });
    } finally {
      setPaymentRequesting(false);
    }
  }, [threadQuote, activeThreadId, fetchMessages]);

  // ── Shared Medical Files ────────────────────────────────────────
  const [sharedFiles, setSharedFiles] = useState([]);

  useEffect(() => {
    if (!activeThreadId) { setSharedFiles([]); return; }
    const _t = '';
    api.get(`/clinic/threads/${activeThreadId}/medical-files`, {
      headers: {},
    })
      .then(({ data }) => data).catch(() => ({ files: [] }))
      .then(d => setSharedFiles(d.files || []))
      .catch(() => setSharedFiles([]));
  }, [activeThreadId, messages.length]);

  const [medicalPreview, setMedicalPreview] = useState(null);

  const closeMedicalPreview = useCallback(() => {
    setMedicalPreview((prev) => {
      if (prev?.url) { try { URL.revokeObjectURL(prev.url); } catch { /* noop */ } }
      return null;
    });
  }, []);

  // Release the object URL if the page unmounts while a preview is open.
  const medicalPreviewUrlRef = useRef(null);
  medicalPreviewUrlRef.current = medicalPreview?.url || null;
  useEffect(() => () => {
    if (medicalPreviewUrlRef.current) {
      try { URL.revokeObjectURL(medicalPreviewUrlRef.current); } catch { /* noop */ }
    }
  }, []);

  const handleViewFile = useCallback(async (attachmentId, fileName) => {
    // UAT-MEDICAL-FILE-OPEN-BLOB-02 — the bytes come from the PROTECTED
    // endpoint (never a public URL), but they must be rendered INSIDE this
    // document. Handing a `blob:` URL to a new top-level browsing context
    // (`window.open`) is what produced `blob:https://treatx.health/<uuid>
    // ERR_FAILED`: Chromium's blob-URL partitioning forces `noopener` on
    // such navigations, so the new tab cannot resolve an object URL minted
    // in this context. Read the Axios blob (`r.data`) and
    // show it in-page; the object URL is revoked when the preview closes.
    // Read the Axios payload via `r.data`, never the Fetch-style accessor.
    try {
      const r = await api.get(`/attachments/${attachmentId}`, {
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(r.data);
      setMedicalPreview({
        url: objectUrl,
        name: fileName,
        type: r.data?.type || '',
        attachmentId,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast({ title: t('provider.messaging.toast.accessDeniedTitle'), description: t('provider.messaging.toast.accessDeniedBody'), variant: 'destructive' });
      } else {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.loadFileFailed'), variant: 'destructive' });
      }
    }
  }, [t]);

  // P1-WA-MEDIA-01 — inbound WhatsApp media. Bytes come from the ACL-enforced
  // canonical route and are rendered IN-PAGE through the same preview surface
  // as shared medical files (never a new top-level context, never a public URL).
  const handleViewWhatsAppMedia = useCallback(async (mediaId, fileName) => {
    if (!activeThreadId || !mediaId) return;
    try {
      const r = await api.get(
        `/v2/threads/${activeThreadId}/whatsapp-media/${encodeURIComponent(mediaId)}`,
        { responseType: 'blob' },
      );
      const objectUrl = URL.createObjectURL(r.data);
      setMedicalPreview({
        url: objectUrl,
        name: fileName,
        type: r.data?.type || '',
        attachmentId: null,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast({ title: t('provider.messaging.toast.accessDeniedTitle'), description: t('provider.messaging.toast.accessDeniedBody'), variant: 'destructive' });
      } else if (status === 503) {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.whatsappMediaUnavailable'), variant: 'destructive' });
      } else {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.loadFileFailed'), variant: 'destructive' });
      }
    }
  }, [activeThreadId, t]);
  const handleResolve = useCallback(async () => {
    if (!activeThreadId) return;
    try {
      await MessagingAPI.resolveThread(activeThreadId);
      toast({ title: t('provider.messaging.toast.resolvedTitle'), description: t('provider.messaging.toast.resolvedBody') });
      // UAT-MSG-MARK-RESOLVED-01 — reflect the canonical new status on the
      // header immediately; the refetches below reconcile with the server.
      setActiveThread((cur) => (cur && cur.id === activeThreadId ? { ...cur, status: 'resolved' } : cur));
      setThreads((prev) => prev.map((th) => (th.id === activeThreadId ? { ...th, status: 'resolved' } : th)));
      fetchMessages(activeThreadId);
      fetchThreads(true);
    } catch (err) {
      // UAT-MSG-MARK-RESOLVED-01 — a denied resolve must say so, instead of
      // the generic failure that made an ACL denial look like a dead button.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast({ title: t('provider.messaging.toast.accessDeniedTitle'), description: t('provider.messaging.toast.accessDeniedBody'), variant: 'destructive' });
      } else {
        toast({ title: t('provider.messaging.toast.errorTitle'), description: t('provider.messaging.toast.resolveFailed'), variant: 'destructive' });
      }
    }
  }, [activeThreadId, fetchMessages, fetchThreads, t]);

  // ── View Lead (UAT-MSG-VIEW-LEAD-01) ──────────────────────────
  // The Lead is resolved through the SAME journey lineage the medical
  // authority uses — thread → conversation_group_id → root_lead_id — so two
  // journeys for one Patient at one Provider can never cross-select.
  //
  // VIEW-LEAD-WORKSPACE-01 — the destination must be the CURRENT user's own
  // authorized workspace. `/clinic/admin/leads/:id` is admitted only for
  // provider_admin / provider_coordinator, so a Sales Rep was routed to a page
  // their role cannot open — which is what produced the admin dashboard and its
  // "operational summary could not be loaded" state. A representative is taken
  // to their own lead in Sales, through the deep link that page already reads.
  const handleViewLead = useCallback(async () => {
    if (!activeThreadId) return;
    try {
      const r = await api.get(`/clinic/threads/${activeThreadId}/root-lead`);
      const leadId = r?.data?.lead_id;
      if (!leadId) throw new Error('no_lead');
      // UAT-MSG-VIEW-LEAD-02 — route by what the caller may actually open:
      // `/clinic/admin/leads/:id` is guarded for admin/coordinator, a rep gets
      // their own lead workspace. Role comes from the canonical RBAC context,
      // never from a loose localStorage key.
      const target = (isAdmin || hasRole('provider_coordinator'))
        ? `/clinic/admin/leads/${encodeURIComponent(leadId)}`
        : `/clinic/sales?lead=${encodeURIComponent(leadId)}`;
      navigate(target);
    } catch (err) {
      const status = err?.response?.status;
      toast({
        title: t('provider.messaging.toast.errorTitle'),
        description: status === 403
          ? t('provider.messaging.toast.accessDeniedBody')
          : t('provider.messaging.toast.noLeadForThread'),
        variant: 'destructive',
      });
    }
  }, [activeThreadId, navigate, t, isAdmin, hasRole]);

  // ── Filter threads ─────────────────────────────────────────────
  // MESSAGING-FILTERS-TRUTH-01 — `thread_type` is the thread's PERSISTENT
  // creation kind: a lead conversation keeps `thread_type='lead'` for its whole
  // life, even after an offer has been sent, accepted and paid. Filtering
  // "Offers" on that literal therefore hid every real offer conversation. The
  // canonical lead stage already projected onto the thread (`lead_status`) is
  // what says whether an offer exists, so a lead conversation carrying an offer
  // now appears under BOTH Leads (it is still that lead's conversation) and
  // Offers (it has one). No parallel offer index is built.
  const filtered = useMemo(() => {
    return threads.filter((t) => {
      // Canonical `has_offer` comes from the backend thread projection
      // (services/offer_projection.offer_exists_for_lead_status) — the inbox
      // does not re-derive the rule from a status list of its own.
      const hasOffer = t.has_offer === true || t.thread_type === 'offer';
      if (threadFilter === 'escalated' && t.status !== 'escalated') return false;
      if (threadFilter === 'unread' && !(t.unread_counts?.[userId] > 0)) return false;
      if (threadFilter === 'offer' && !hasOffer) return false;
      if (threadFilter === 'lead' && !isSalesContextThread(t)) return false;
      if (threadFilter === 'flagged' && !t.circumvention_flag) return false;
      if (searchQ) {
        const q = searchQ.toLowerCase();
        const name = t.participants?.find((p) => p.role === 'patient')?.name?.toLowerCase() || '';
        const subj = t.subject?.toLowerCase() || '';
        return name.includes(q) || subj.includes(q);
      }
      return true;
    });
  }, [threads, threadFilter, userId, searchQ]);

  // ── Group threads for visual organization ─────────────────────
  const groupedThreads = useMemo(() => {
    // B-3 / INV-A5 — SYSTEM_POOL is the canonical sentinel for
    // "unassigned". UAT-MSG-IDENTITY lock 7 — only sales-context threads
    // can be "Unassigned"; ownerless coordination/medical/system threads
    // are listed separately, never as unassigned sales leads.
    const noOwner = (t) => !t.assigned_agent_id || t.assigned_agent_id === 'SYSTEM_POOL';
    const notTransferred = (t) => t.transfer_status !== 'transferred';
    const unassigned = filtered.filter(t => noOwner(t) && notTransferred(t) && t.clinic_id && isSalesContextThread(t));
    const other = filtered.filter(t => noOwner(t) && notTransferred(t) && !(t.clinic_id && isSalesContextThread(t)));
    const assigned = filtered.filter(t => !noOwner(t) && notTransferred(t));
    const transferred = filtered.filter(t => t.transfer_status === 'transferred');
    return { unassigned, assigned, transferred, other };
  }, [filtered]);

  const showGroups = inboxScope === 'all' && !searchQ && threadFilter === 'all';

  // ── Auth guard ─────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-required">
        <Card className="max-w-sm">
          <CardContent className="p-8 text-center">
            <Building2 className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-1">{t('provider.messaging.loginRequiredTitle')}</h2>
            <p className="text-sm text-slate-500 mb-4">{t('provider.messaging.loginRequiredBody')}</p>
            <Button onClick={() => navigate('/login')} data-testid="login-redirect-btn">{t('provider.messaging.logInBtn')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="h-[calc(100vh-80px)] bg-slate-100 flex flex-col" data-testid="clinic-messages-page">
      {/* Top Bar */}
      <header className="bg-slate-900 text-white px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-800 rounded-full" data-testid="nav-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-sm">{t('provider.messaging.pageTitle')}</h1>
          <p className="text-[11px] text-slate-400 truncate">{user.clinicName}</p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Wifi className="h-4 w-4 text-emerald-400" data-testid="transport-connected" title={t('provider.messaging.connected', { transport: transport.toUpperCase() })} />
          ) : (
            <WifiOff className="h-4 w-4 text-red-400" data-testid="transport-disconnected" title={t('provider.messaging.disconnected')} />
          )}
          <Button variant="ghost" size="sm" onClick={() => fetchThreads(true)} className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8 p-0" data-testid="refresh-btn" title={t('provider.messaging.refresh')}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* UAT-PROD-PROVIDER-MESSAGING-ACCESS-01 — deep-link outcome state.
          A missing or unauthorized canonical conversation must say so;
          the UI never creates a replacement thread. */}
      {deepLinkError && (
        <div
          className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-start gap-2 shrink-0"
          role="alert"
          data-testid="deep-link-error-banner"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900 flex-1">
            {deepLinkError === 'not_authorized'
              ? t('provider.messaging.deepLink.notAuthorized', 'You are not assigned to this conversation, so it cannot be opened. Ask an administrator to assign it to you.')
              : deepLinkError === 'not_found'
                ? t('provider.messaging.deepLink.notFound', 'No conversation exists for this lead yet. It will appear here once the first message is exchanged.')
                : t('provider.messaging.deepLink.error', 'This conversation could not be opened right now. Please try again.')}
          </p>
          <button
            onClick={() => { setDeepLinkError(null); setSearchParams({}, { replace: true }); }}
            className="text-amber-700 hover:text-amber-900 text-xs underline shrink-0"
            data-testid="deep-link-error-dismiss"
          >
            {t('provider.messaging.dismiss', 'Dismiss')}
          </button>
        </div>
      )}
      {/* Stats */}
      <StatsBar threads={threads} userId={userId} />

      {/* Split Pane */}
      <div className="flex-1 flex overflow-hidden">
        {/* ── INBOX PANEL ─────────────────────────────────── */}
        <div className={`w-full lg:w-[360px] xl:w-[400px] bg-white border-r flex flex-col shrink-0 ${mobileShowInbox ? '' : 'hidden lg:flex'}`} data-testid="inbox-panel">
          {/* Phase C: Inbox Scope Tabs */}
          <div className="p-3 border-b">
            <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
              {[
                { id: 'all', label: t('provider.messaging.scopeAll') },
                { id: 'mine', label: t('provider.messaging.scopeMine') },
                { id: 'unassigned', label: t('provider.messaging.scopeUnassigned') },
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={`inbox-scope-${tab.id}`}
                  onClick={() => setInboxScope(tab.id)}
                  className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                    inboxScope === tab.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search + Filters */}
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('provider.messaging.searchPatientsPlaceholder')}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="pl-9 h-8 text-sm"
                data-testid="inbox-search"
              />
            </div>
            <Tabs value={threadFilter} onValueChange={setThreadFilter} className="w-full">
              <TabsList className="grid grid-cols-6 h-7">
                <TabsTrigger value="all" className="text-[10px] px-1 min-w-0 truncate" data-testid="filter-all">{t('provider.messaging.filterAll')}</TabsTrigger>
                <TabsTrigger value="unread" className="text-[10px] px-1 min-w-0 truncate" data-testid="filter-unread">{t('provider.messaging.filterUnread')}</TabsTrigger>
                <TabsTrigger value="lead" className="text-[10px] px-1 min-w-0 truncate" data-testid="filter-lead">{t('provider.messaging.filterLeads')}</TabsTrigger>
                <TabsTrigger value="offer" className="text-[10px] px-1 min-w-0 truncate" data-testid="filter-offer">{t('provider.messaging.filterOffers')}</TabsTrigger>
                <TabsTrigger value="flagged" className="text-[10px] px-1" data-testid="filter-flagged">
                  <Shield className="h-3 w-3" />
                </TabsTrigger>
                <TabsTrigger value="escalated" className="text-[10px] px-1 text-red-500" data-testid="filter-escalated">!</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto" data-testid="thread-list">
            {/* MSG-09 P1-4 — scoped-visibility banner above a NON-empty
                participant-scoped list, so the rep knows the list is
                partial instead of assuming it is complete. */}
            {listNotice === 'scoped' && filtered.length > 0 && (
              <div
                className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-100"
                data-testid="inbox-scoped-notice"
              >
                <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-amber-800">{t('provider.messaging.inboxNotice.scopedTitle')}</p>
                  <p className="text-[11px] text-amber-700">{t('provider.messaging.inboxNotice.scopedBody')}</p>
                </div>
              </div>
            )}
            {threadsLoading && threads.length === 0 ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-7 w-7 animate-spin text-sky-500" />
              </div>
            ) : filtered.length === 0 ? (
              <EmptyInbox notice={listNotice} onRetry={() => fetchThreads(true)} />
            ) : (
              <>
                {showGroups ? (
                  <>
                    {/* Unassigned threads first (attention needed) */}
                    {groupedThreads.unassigned.length > 0 && (
                      <>
                        <ThreadGroupHeader label={t('provider.messaging.groupUnassigned')} count={groupedThreads.unassigned.length} color="bg-orange-50 text-orange-700" />
                        {groupedThreads.unassigned.map((thread) => (
                          <ThreadListItem key={thread.id} thread={thread} isActive={activeThreadId === thread.id}
                            onClick={() => selectThread(thread)} userId={userId} clinicAgents={clinicAgents} leadIndex={leadIndex} />
                        ))}
                      </>
                    )}
                    {/* Transferred threads */}
                    {groupedThreads.transferred.length > 0 && (
                      <>
                        <ThreadGroupHeader label={t('provider.messaging.groupTransferred')} count={groupedThreads.transferred.length} color="bg-purple-50 text-purple-700" />
                        {groupedThreads.transferred.map((thread) => (
                          <ThreadListItem key={thread.id} thread={thread} isActive={activeThreadId === thread.id}
                            onClick={() => selectThread(thread)} userId={userId} clinicAgents={clinicAgents} leadIndex={leadIndex} />
                        ))}
                      </>
                    )}
                    {/* Assigned threads */}
                    {groupedThreads.assigned.length > 0 && (
                      <>
                        <ThreadGroupHeader label={t('provider.messaging.groupAssigned')} count={groupedThreads.assigned.length} color="bg-sky-50 text-sky-700" />
                        {groupedThreads.assigned.map((thread) => (
                          <ThreadListItem key={thread.id} thread={thread} isActive={activeThreadId === thread.id}
                            onClick={() => selectThread(thread)} userId={userId} clinicAgents={clinicAgents} leadIndex={leadIndex} />
                        ))}
                      </>
                    )}
                    {/* Ownerless non-sales threads (coordination/medical/system) — not "unassigned sales leads" */}
                    {groupedThreads.other.length > 0 && (
                      <>
                        <ThreadGroupHeader label={t('provider.messaging.groupOther')} count={groupedThreads.other.length} color="bg-slate-50 text-slate-600" />
                        {groupedThreads.other.map((thread) => (
                          <ThreadListItem key={thread.id} thread={thread} isActive={activeThreadId === thread.id}
                            onClick={() => selectThread(thread)} userId={userId} clinicAgents={clinicAgents} leadIndex={leadIndex} />
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  filtered.map((thread) => (
                    <ThreadListItem key={thread.id} thread={thread} isActive={activeThreadId === thread.id}
                      onClick={() => selectThread(thread)} userId={userId} clinicAgents={clinicAgents} leadIndex={leadIndex} />
                  ))
                )}
                {/* Infinite scroll sentinel */}
                {hasMoreThreads && (
                  <div ref={inboxSentinelRef} className="flex justify-center py-3">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── THREAD PANEL ────────────────────────────────── */}
        <div className={`flex-1 min-w-0 flex flex-col bg-slate-50 ${mobileShowInbox ? 'hidden lg:flex' : 'flex'}`} data-testid="thread-panel">
          {activeThread ? (
            <>
              <ThreadHeader
                thread={activeThread}
                onResolve={handleResolve}
                onViewLead={handleViewLead}
                onBack={() => { setMobileShowInbox(true); setActiveThreadId(null); setActiveThread(null); }}
                riskSummary={riskSummary}
                onThreadUpdate={(updated) => {
                  setActiveThread(updated);
                  setThreads(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
                }}
                isAdmin={isAdmin}
                clinicAgents={clinicAgents}
                leadIndex={leadIndex}
                threadQuote={threadQuote}
              />

              {/* Stage Banner */}
              <StageBanner policy={policy} />

              {/* Messages */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-3" data-testid="message-list">
                {/* Load older trigger */}
                {hasMoreMsgs && (
                  <div className="flex justify-center mb-3">
                    <button
                      onClick={loadOlder}
                      disabled={loadingOlder}
                      className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 bg-white border rounded-full px-3 py-1 shadow-sm disabled:opacity-50"
                      data-testid="load-older-btn"
                    >
                      {loadingOlder ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronUp className="h-3 w-3" />}
                      {t('provider.messaging.loadOlderMessages')}
                    </button>
                  </div>
                )}

                {msgsLoading ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="h-7 w-7 animate-spin text-sky-500" />
                  </div>
                ) : (
                  <>
                    {groupConsecutiveSystemEvents(messages).map((item) => {
                      const renderBubble = (msg) => {
                        const idx = messages.indexOf(msg);
                        const fromClinic = CLINIC_ROLES.has(msg.sender_role);
                        const showSender = idx === 0
                          || messages[idx - 1]?.sender_id !== msg.sender_id;
                        return (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            fromClinic={fromClinic}
                            showSender={showSender}
                            isAdmin={isAdmin}
                            onViewWhatsAppMedia={handleViewWhatsAppMedia}
                          />
                        );
                      };
                      if (item.kind === 'system-group') {
                        return (
                          <SystemEventGroup
                            key={item.key}
                            groupId={item.messages[0].id}
                            count={item.messages.length}
                          >
                            {item.messages.map(renderBubble)}
                          </SystemEventGroup>
                        );
                      }
                      return renderBubble(item.message);
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Shared Medical Files */}
              {sharedFiles.length > 0 && (
                <div className="bg-blue-50 border-t border-blue-200 px-4 py-2" data-testid="shared-medical-files">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Shield className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[11px] font-medium text-blue-700">{t('provider.messaging.sharedMedicalFiles', { count: sharedFiles.length })}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {sharedFiles.map(f => (
                      <button
                        key={f.attachment_id}
                        onClick={() => handleViewFile(f.attachment_id, f.file_name)}
                        className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-md px-2 py-1 text-[11px] text-blue-700 hover:bg-blue-100 transition-colors"
                        data-testid={`shared-file-${f.attachment_id}`}
                      >
                        <FileImage className="h-3 w-3" />
                        <span className="truncate max-w-[120px]">{f.file_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Request — gated by canonical server-side readiness.
                  STAGE 2: the button is no longer offered on the strength of a
                  quote existing. `PaymentReadinessBar` asks the backend whether
                  a verified PSP route exists, and the backend re-checks again at
                  the mutation boundary, so the provider sees the blocker instead
                  of the patient meeting an unpayable checkout. The old hardcoded
                  `|| 'EUR'` display fallback is gone: the currency now comes
                  from the quotation's own canonical totals. */}
              {threadQuote && threadQuote.payment_request_status !== 'requested' && threadQuote.payment_request_status !== 'paid' && (
                <PaymentReadinessBar
                  quote={threadQuote}
                  onRequested={() => {
                    toast({ title: t('provider.messaging.toast.paymentRequestedTitle'), description: t('provider.messaging.toast.paymentRequestedBody') });
                    setThreadQuote(prev => prev ? { ...prev, payment_request_status: 'requested' } : null);
                    if (activeThreadId) fetchMessages(activeThreadId);
                  }}
                />
              )}
              {threadQuote?.payment_request_status === 'requested' && (
                <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-2 flex items-center gap-2" data-testid="payment-requested-badge">
                  <CheckCheck className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">{t('provider.messaging.paymentRequestSentWaiting')}</span>
                </div>
              )}

              {/* FASTFIX-PRE-OFFER-MESSAGING-UNBLOCK-01 (2026-06-12):
                  The canonical product rule is that TreatX-internal
                  messaging is open from lead creation onward — pre-offer
                  clarification, document requests, treatment-context
                  exchange.  External-channel leakage and WhatsApp
                  handoff remain stage-gated separately (see Stage Banner
                  + payment-instruction guard + contact-reveal masking).
                  The legacy pre-quote lock banner is therefore removed;
                  the composer always renders (still disabled for
                  resolved threads). */}
              {/* MID-04-B / FE-01+FE-02 — WhatsApp channel identity, canonical
                  send eligibility + lock reason and the originating Provider
                  number, rendered in the EXISTING conversation view. */}
              <WhatsAppChannelStrip
                thread={activeThread}
                policy={policy}
                senderNumber={activeThreadWaSenderNumber}
                canSend={canSendInActiveThread}
              />
              <ComposeBox
                onSend={handleSend}
                disabled={sending || activeThread.status === 'resolved'}
                senderName={userName}
                onTyping={() => sendTyping(activeThreadId)}
                typingUser={activeThreadId && typingUsers[activeThreadId]?.ts > Date.now() - 4000
                  ? typingUsers[activeThreadId] : null}
                threadId={activeThreadId}
                recipientLabel={activeThreadPatientLabel}
                actorRole={activeRoleId || undefined}
                locale={i18n.language || 'en'}
                onSharedDoc={() => { fetchMessages(activeThreadId); }}
              />
            </>
          ) : (
            <EmptyThread />
          )}
        </div>
      </div>

      {/* Shared medical file preview — rendered IN-PAGE (never a new tab) */}
      {medicalPreview && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeMedicalPreview}
          data-testid="medical-file-preview-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-2.5 border-b">
              <Shield className="h-4 w-4 text-blue-600 shrink-0" />
              <span className="text-sm font-medium text-slate-800 truncate" data-testid="medical-file-preview-name">
                {medicalPreview.name}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <a
                  href={medicalPreview.url}
                  download={medicalPreview.name || 'medical-file'}
                  className="text-xs text-blue-700 hover:underline"
                  data-testid="medical-file-preview-download"
                >
                  {t('common.download', 'Download')}
                </a>
                <button
                  onClick={closeMedicalPreview}
                  className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
                  data-testid="medical-file-preview-close"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center p-3">
              {medicalPreview.type.startsWith('image/') ? (
                <img
                  src={medicalPreview.url}
                  alt={medicalPreview.name}
                  className="max-w-full max-h-[75vh] object-contain"
                  data-testid="medical-file-preview-image"
                />
              ) : medicalPreview.type.startsWith('video/') ? (
                <video
                  src={medicalPreview.url}
                  controls
                  className="max-w-full max-h-[75vh]"
                  data-testid="attachment-preview-video"
                />
              ) : medicalPreview.type.startsWith('audio/') ? (
                <audio
                  src={medicalPreview.url}
                  controls
                  className="w-full"
                  data-testid="attachment-preview-audio"
                />
              ) : (
                <iframe
                  src={medicalPreview.url}
                  title={medicalPreview.name}
                  className="w-full h-[75vh] bg-white"
                  data-testid="medical-file-preview-frame"
                />
              )}
            </div>
            {medicalPreview.attachmentId && (
              <MedicalAccessRequestPanel
                threadId={activeThreadId}
                attachmentId={medicalPreview.attachmentId}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicMessagesV2;
